import { useGetAllVetterEntitiesQuery } from '@codezo/graphql';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface VetterPickerModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	selectedIds: string[];
	onSave: (ids: string[]) => void;
	loading?: boolean;
}

export function VetterPickerModal({
	open,
	onOpenChange,
	selectedIds,
	onSave,
	loading: saving,
}: VetterPickerModalProps) {
	const [draft, setDraft] = useState<string[]>([]);
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);

	const { data, loading } = useGetAllVetterEntitiesQuery({
		variables: {
			input: {
				pagination: { currentPage: page, limit: 20 },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: !open,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllVetterEntity?.data,
		pageInfo: data?.getAllVetterEntity?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	useEffect(() => {
		if (open) {
			setDraft(selectedIds);
			setQuery('');
			scroll.reset();
			setPage(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed the draft only when the modal opens, not on selectedIds changes mid-edit
	}, [open]);

	const toggle = (id: string) => {
		setDraft((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Vetters</DialogTitle>
					<DialogDescription>Select which vetters should be assigned.</DialogDescription>
				</DialogHeader>

				<DialogBody className="flex flex-col gap-0 p-0">
					<div className="relative shrink-0 px-4 pt-4 pb-2">
						<Search
							size={14}
							className="pointer-events-none absolute left-7 top-1/2 -translate-y-1/2 text-text-4"
						/>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search vetters…"
							className="h-8 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-9 pr-3 text-base text-text-1 outline-none placeholder:text-text-4 focus:border-line-3"
							autoFocus
						/>
					</div>

					<div className="scroll-thin h-[260px] overflow-y-auto px-4 py-1">
						<div className="flex flex-col gap-0.5">
							{scroll.items.map((entity) => {
								const isSelected = draft.includes(entity.id);
								return (
									<button
										key={entity.id}
										onClick={() => toggle(entity.id)}
										className={cn(
											'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover',
											isSelected && 'bg-surface-fill'
										)}
									>
										<Checkbox
											checked={isSelected}
											tabIndex={-1}
											className="pointer-events-none"
										/>
										<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill font-mono text-2xs font-medium text-text-2">
											{entity.name
												.split(' ')
												.map((w) => w[0])
												.slice(0, 2)
												.join('')
												.toUpperCase()}
										</div>
										<span className="text-base font-medium text-text-1">
											{entity.name}
										</span>
									</button>
								);
							})}
							{scroll.initialLoading && (
								<div className="flex items-center justify-center py-6 text-sm text-text-4">
									Loading…
								</div>
							)}
							{!loading && scroll.items.length === 0 && !scroll.initialLoading && (
								<div className="flex items-center justify-center py-6 text-center text-sm text-text-4">
									{query
										? 'No vetters match your search.'
										: 'No vetters available.'}
								</div>
							)}
							{scroll.loadingMore && (
								<div className="py-2 text-center text-xs text-text-4">
									Loading more…
								</div>
							)}
							<div ref={scroll.sentinelRef} className="h-1" />
						</div>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={() => onSave(draft)} disabled={saving}>
						{saving ? 'Saving…' : `Save (${draft.length})`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
